import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiCogOutline } from "@mdi/js";
import { Button, SearchField } from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { feedbackStore } from "@/stores/FeedbackStore";
import FilterPanel from "./components/FilterPanel";
import FeedbackTable from "./components/FeedbackTable";
import FeedbackDrawer from "./components/FeedbackDrawer";
import { useFeedbackDrawerUrl } from "./hooks/useFeedbackDrawerUrl";
import "./FeedbackPage.css";

const FeedbackPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const {
    ticketUuid,
    startWork,
    openTicket,
    closeTicket,
    clearStartWorkParam,
  } = useFeedbackDrawerUrl();
  const [searchInput, setSearchInput] = useState(() => feedbackStore.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isInitialMount = useRef(true);
  const startWorkHandledRef = useRef<string | null>(null);
  const urlSyncRef = useRef<string | null>(null);

  useEffect(() => {
    feedbackStore.loadAll();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    feedbackStore.setIsUserTypingSearch(true);
    debounceRef.current = setTimeout(() => {
      feedbackStore.setIsUserTypingSearch(false);
      feedbackStore.setSearch(searchInput);
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    if (!ticketUuid) {
      startWorkHandledRef.current = null;
      urlSyncRef.current = null;
      if (feedbackStore.isDrawerOpen) {
        feedbackStore.resetDrawer();
      }
      return;
    }

    const syncKey = `${ticketUuid}:${startWork ? "1" : "0"}`;
    if (urlSyncRef.current === syncKey) return;
    urlSyncRef.current = syncKey;

    const shouldStartWork =
      startWork && startWorkHandledRef.current !== ticketUuid;

    void (async () => {
      const opened = await feedbackStore.openDrawer(ticketUuid, {
        startWork: shouldStartWork,
      });

      if (!opened) {
        closeTicket();
        return;
      }

      if (shouldStartWork) {
        startWorkHandledRef.current = ticketUuid;
        clearStartWorkParam();
        urlSyncRef.current = `${ticketUuid}:0`;
      }
    })();
  }, [ticketUuid, startWork, closeTicket, clearStartWorkParam]);

  const handleCloseTicket = () => {
    closeTicket();
  };

  return (
    <div className="fb-page" data-testid="feedback-page">
      <div className="fb-page__toolbar">
        <div className="fb-page__toolbar-left">
          <div className="fb-page__search">
            <SearchField
              value={searchInput}
              onChange={(v) => setSearchInput(v)}
              aria-label="Поиск обращений"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Поиск..." />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>
          <FilterPanel />
        </div>
        <Button
          isIconOnly
          variant="ghost"
          className="fb-page__settings-btn icon-btn--card"
          aria-label="Настройки обратной связи"
          onPress={() => navigate(ROUTES.FEEDBACK_SETTINGS)}
        >
          <Icon path={mdiCogOutline} size={0.9} />
        </Button>
      </div>
      <div className="fb-page__table-wrap">
        <FeedbackTable onOpenTicket={openTicket} />
      </div>
      <FeedbackDrawer onClose={handleCloseTicket} />
    </div>
  );
});

FeedbackPage.displayName = "FeedbackPage";
export default FeedbackPage;
